import { useSessionStorageState } from 'ahooks';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { type CSSProperties, type MouseEvent, memo, useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import type { BannerItemType } from '@/app/components/shared/Community';
import { Button } from '@/app/components/ui/button';
import { ACTIVITY_PAGE_FROM_KEY, BANNER_DELAY } from '@/app/constants';
import { useExpose } from '@/app/hooks';
import { ActivityPageFromEnum } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useRouter } from '@/i18n/routing';

function internalBannerCarousel({
  list,
  aspectRatio,
}: {
  list: BannerItemType[];
  aspectRatio?: CSSProperties['aspectRatio'];
}) {
  const [selectedIndex, setSelectedIndex] = useState(0); //当前轮播坐标
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]); //用于生成原点指示器
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true); //禁用“上一张”
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true); //禁用“下一张”
  const carouselWrapperRef = useRef<HTMLDivElement>(null); //轮播item的容器

  const autoplayRef = useRef<ReturnType<typeof Autoplay>>(Autoplay({ delay: BANNER_DELAY, stopOnInteraction: false }));
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [autoplayRef.current]);
  const router = useRouter();

  const [_, setActivityPageFrom] = useSessionStorageState<ActivityPageFromEnum>(ACTIVITY_PAGE_FROM_KEY, {
    defaultValue: ActivityPageFromEnum.OUTSIDE,
  });

  useEffect(() => {
    if (!emblaApi) return;
    const onInit = () => setScrollSnaps(emblaApi.scrollSnapList());
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap()); //更新 selectedIndex
      setPrevBtnDisabled(!emblaApi.canScrollPrev()); //更新“上一张”禁用标志位
      setNextBtnDisabled(!emblaApi.canScrollNext()); //更新“下一张”禁用标志位
    };
    onInit();
    onSelect();
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect); //reInit - 重新初始化，select - index发生变化
  }, [emblaApi]);

  useExpose(
    (index: number) => {
      reportEvent('banner_show', {
        index: index + 1,
        banner_name: list[index].title || '',
        banner_id: list[index].id || '',
        activity_id: list[index].activity_id || '',
      });
    },
    carouselWrapperRef.current?.querySelectorAll('.carousel-item'),
    list,
  );

  //点击上一张
  const handleClickPrevButton = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    emblaApi?.scrollPrev();
    autoplayRef.current?.reset(); // 重置计时器
  };

  //点击下一张
  const handleClickNextButton = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    emblaApi?.scrollNext();
    autoplayRef.current?.reset(); // 重置计时器
  };

  //点击圆点指示器
  const handleClickDotButton = (index: number, e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    emblaApi?.scrollTo(index);
    autoplayRef.current?.reset(); // 重置计时器
  };

  //点击轮播图
  const handleClickCarouselItem = () => {
    const { id, type, redirect_url, activity_id, title } = list[selectedIndex] || {};
    if (type === 'activity' && activity_id) {
      router.push(`/activity?id=${activity_id}`);
    } else if (redirect_url) {
      openOrRedirect(redirect_url);
    }
    setActivityPageFrom(ActivityPageFromEnum.INSIDE);
    reportEvent('banner_click', {
      banner_id: id || '',
      activity_id: activity_id || '',
      index: selectedIndex + 1,
      banner_position: selectedIndex + 1,
      banner_name: title || '',
    });
  };

  return (
    <div className="group relative flex-1 cursor-pointer" onClick={handleClickCarouselItem}>
      {/* 轮播图片 */}
      <div
        ref={emblaRef}
        className="h-full w-full overflow-hidden rounded-14"
        style={{
          aspectRatio: aspectRatio || '16/9',
        }}
      >
        <div ref={carouselWrapperRef} className="flex h-full">
          {list.map(({ cover_uri, title }, index) => (
            <div
              key={`image-${index}-${title}`}
              className="carousel-item relative min-w-0 flex-none basis-full"
              data-index={index}
            >
              {cover_uri ? (
                <img
                  src={cover_uri}
                  alt={title || 'Vidu AI video generation banner'}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-[#343C48]" />
              )}
              {/* <div
                className="absolute right-0 bottom-0 left-0 h-[90px] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.6)_100%)] p-5 text-white sm:h-[70px] sm:p-2"
                style={{ paddingRight: `${scrollSnaps.length * 20}px` }}
              >
                <div className="line-clamp-1 font-semibold text-2xl sm:text-base">{title}</div>
                <div className="mt-[2px] line-clamp-1 text-sm sm:mt-0 sm:text-xs">{subtitle}</div>
              </div> */}
            </div>
          ))}
        </div>
      </div>

      {/* 左右切换按钮 */}
      {list.length > 1 && (
        <div className="-translate-y-1/2 absolute top-1/2 flex w-full transform items-center justify-between px-4 opacity-0 group-hover:opacity-100 sm:hidden">
          <Button
            className="-translate-y-1/2 group absolute top-[50%] bottom-0 left-2 z-10 h-9 w-9 rounded-8 border border-system-white10 bg-system-white10 p-2.5 backdrop-blur-[20px] hover:bg-system-white20 disabled:hover:bg-system-white10 sm:hidden"
            onClick={handleClickPrevButton}
            disabled={prevBtnDisabled}
          >
            <BaseLineLeft className="size-5 text-system-white group-disabled:text-system-white20" />
          </Button>
          <Button
            className="-translate-y-1/2 group absolute top-[50%] right-2 bottom-0 z-10 h-9 w-9 rounded-8 border border-system-white10 bg-system-white10 p-2.5 backdrop-blur-[20px] hover:bg-system-white20 disabled:hover:bg-system-white10 sm:hidden"
            onClick={handleClickNextButton}
            disabled={nextBtnDisabled}
          >
            <BaseLineRight className="size-5 text-system-white group-disabled:text-system-white20" />
          </Button>
        </div>
      )}

      {/* 底部圆点指示器 */}
      {list.length > 1 && (
        <div className="absolute right-5 bottom-5 z-10 sm:right-2 sm:bottom-2">
          <div className="flex items-center gap-1.5 rounded-full sm:gap-[3px]">
            {scrollSnaps.map((_, index) => (
              <button
                key={`button-indicator-${index}-${list[index].id}`}
                type="button"
                className={`h-1.5 sm:h-[3px] sm:w-[3px] w-${index === selectedIndex ? '4' : '1.5'} ${index === selectedIndex && 'sm:w-[7px]'} rounded-full bg-white ${index === selectedIndex ? 'opacity-100' : 'opacity-50'}`}
                onClick={(e) => handleClickDotButton(index, e)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const ActivityBannerCarousel = memo(internalBannerCarousel, (prevProps, nextProps) => {
  return shallow(prevProps, nextProps);
}) as typeof internalBannerCarousel;
